import { Pinia, Store } from 'pinia-class-component';
import { LanguageAndResourcesVmV1, LocalizationApiUiClient } from '../components/backend/backendapi';
import { initClient } from '../api';

@Store({
  id: 'LocalizationStore',
  name: 'LocalizationStore',
})
export default class LocalizationStore extends Pinia {
  private readonly api = initClient(LocalizationApiUiClient);

  //data
  private _loadingPromise: Promise<void> | null = null;
  private _inheritedLanguages: LanguageAndResourcesVmV1[] = [];
  private _languages: LanguageAndResourcesVmV1[] = [];

  //getter
  get loading(): boolean {
    return this._loadingPromise !== null;
  }

  get inheritedLanguages(): LanguageAndResourcesVmV1[] {
    return this._inheritedLanguages;
  }

  get inheritedLanguagesById(): Map<number, LanguageAndResourcesVmV1> {
    return new Map(this._inheritedLanguages.map(e => [e.language.id!, e]));
  }

  get inheritedLanguagesByLocale(): Map<string, LanguageAndResourcesVmV1> {
    return new Map(this._inheritedLanguages.map(e => [e.language.languageCulture!, e]));
  }

  get languages(): LanguageAndResourcesVmV1[] {
    return this._languages;
  }

  get languagesById(): Map<number, LanguageAndResourcesVmV1> {
    return new Map(this._languages.map(e => [e.language.id!, e]));
  }

  get languagesByLocale(): Map<string, LanguageAndResourcesVmV1> {
    return new Map(this._languages.map(e => [e.language.languageCulture!, e]));
  }

  get locales(): string[] {
    return [...new Set([...this.inheritedLanguages, ...this.languages].map(e => e.language.languageCulture!))]
      .sort((l, r) => {
        const len = l.startsWith('en');
        const ren = r.startsWith('en');
        if (len && !ren) {
          return -1;
        } else if (!len && ren) {
          return 1;
        } else {
          return l.localeCompare(r);
        }
      });
  }

  //actions
  clear(): void {
    this._languages.splice(0);
  }

  setInheritedLanguages(localizations: LanguageAndResourcesVmV1[]): void {
    this._inheritedLanguages.splice(0);
    this._inheritedLanguages.push(...localizations);
  }

  setLanguages(localizations: LanguageAndResourcesVmV1[]): void {
    this._languages.splice(0);
    this._languages.push(...localizations);
  }

  rememberLanguage(localization: LanguageAndResourcesVmV1): void {
    const index = this._languages.findIndex(e => e.language.id === localization.language.id);
    if (index >= 0) {
      this._languages.splice(index, 1, localization);
    } else {
      this._languages.push(localization);
    }
  }

  forgetLanguage(localization: LanguageAndResourcesVmV1): void {
    const index = this._languages.findIndex(e => e.language.id === localization.language.id);
    if (index >= 0) {
      this._languages.splice(index, 1);
    }
  }

  async reload(force: boolean = false): Promise<void> {
    if (this._loadingPromise) {
      await this._loadingPromise;
      if (!force) {// when force is true, wait for the current operation to complete, then reload
        return;
      }
    }

    try {
      const inherited = this.api.inherited();
      const selectedAccount = this.api.selectedAccount();
      this._loadingPromise = Promise.all([inherited, selectedAccount]).then(_ => undefined);
      await this._loadingPromise;
      this.setInheritedLanguages(await inherited);
      this.setLanguages(await selectedAccount);
    } catch (err) {
      this.clear();
    } finally {
      this._loadingPromise = null;
    }
  }

  async loadIfAbsent(): Promise<void> {
    if (this._languages.length > 0 || this._inheritedLanguages.length > 0) {
      return;
    }
    return await this.reload();
  }
}